import { ISLAND_BY_SLUG, ISLANDS } from "../lib/constants";
import { useT } from "../lib/LangContext";

const Row = ({ data, accent = false, tIsland }) => (
  <div
    className={`flex items-center justify-between gap-3 px-3 py-2.5 ${
      accent ? "bg-[#F7F9FA]" : ""
    }`}
    data-testid={`weather-row-${data.island}`}
  >
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-lg leading-none" aria-hidden="true">
        {ISLAND_BY_SLUG[data.island]?.flag}
      </span>
      <span className="text-[13px] text-[#1A1A1A] truncate">
        {tIsland(data.island)}
      </span>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <span className="text-base leading-none" aria-hidden="true" style={{ fontFamily: '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji","EmojiOne Color","Twemoji Mozilla",sans-serif' }}>
        {data.current.icon}
      </span>
      <span className="text-[14px] font-medium text-[#1A1A1A] tabular-nums">
        {data.current.temp}°
      </span>
    </div>
  </div>
);

const SingleIsland = ({ data, tIsland }) => {
  const isl = ISLAND_BY_SLUG[data.island];
  return (
    <div data-testid={`weather-single-${data.island}`}>
      <div
        className="px-4 py-3 rounded-t-[10px] text-white relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${isl?.color || "#0AADA0"} 0%, ${
            isl?.color || "#0AADA0"
          }cc 100%)`,
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] opacity-85">
              {data.city}
            </div>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="text-[34px] font-light leading-none tabular-nums">
                {data.current.temp}°
              </span>
              <span className="text-[12px] opacity-85">
                H {data.today.high}° · L {data.today.low}°
              </span>
            </div>
            <div className="text-[12px] opacity-95 mt-0.5">
              {data.current.label}
            </div>
          </div>
          <div className="text-[36px] leading-none flex-shrink-0" aria-hidden="true" style={{ fontFamily: '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji","EmojiOne Color","Twemoji Mozilla",sans-serif' }}>
            {data.current.icon}
          </div>
        </div>
        <div className="mt-2 flex items-center gap-3 text-[11px] opacity-85">
          <span>💨 {data.current.wind_kmh} km/h</span>
          <span>💧 {data.current.humidity}%</span>
        </div>
      </div>
      <div className="grid grid-cols-3 border border-t-0 border-[#E5E5E5] rounded-b-[10px]">
        {data.forecast.map((d, i) => {
          const dt = new Date(d.date);
          const day = dt.toLocaleDateString(undefined, { weekday: "short" });
          return (
            <div
              key={d.date}
              className={`flex flex-col items-center py-2.5 ${
                i < 2 ? "border-r border-[#E5E5E5]" : ""
              }`}
              data-testid={`weather-forecast-${d.date}`}
            >
              <span className="text-[11px] text-[#6B7280] uppercase tracking-wider">
                {day}
              </span>
              <span className="text-[18px] my-1 leading-none" aria-hidden="true">
                {d.icon}
              </span>
              <span className="text-[12px] text-[#1A1A1A] tabular-nums">
                {d.high}°
                <span className="text-[#6B7280] ml-1">{d.low}°</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const WeatherWidget = ({ weather, island }) => {
  const { tIsland, t } = useT();
  if (!weather || Object.keys(weather).length === 0) return null;

  // Single-island view: rich card. "all" view: compact list of all 4.
  return (
    <div
      className="card-soft overflow-hidden"
      data-testid="weather-widget"
      aria-label={t("weather_label") || "Weather"}
    >
      <div className="px-4 pt-3 pb-1 flex items-center justify-between">
        <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280]">
          {t("weather_label") || "Weather"}
        </h3>
        <span className="text-[10px] text-[#9CA3AF]">openweathermap</span>
      </div>
      {island !== "all" && weather[island] ? (
        <div className="p-3 pt-2">
          <SingleIsland data={weather[island]} tIsland={tIsland} />
        </div>
      ) : (
        <div className="pb-2">
          {ISLANDS.filter((i) => i.slug !== "all" && weather[i.slug]).map(
            (i, idx) => (
              <Row
                key={i.slug}
                data={weather[i.slug]}
                accent={idx % 2 === 1}
                tIsland={tIsland}
              />
            )
          )}
        </div>
      )}
    </div>
  );
};
